import { Download } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

export default function InstagramPost() {
  const download = async () => {
    const node = document.getElementById("ig-svg-wrap");
    if (!node) return;
    try {
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        width: 1080,
        height: 1350,
        canvasWidth: 1080,
        canvasHeight: 1350,
        skipFonts: false,
        style: { transform: "none" },
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "aavaa-instagram-post.png";
      a.click();
    } catch (e) {
      toast.error("PNG export failed — try again in a moment.");
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-[#2F3630] text-white p-6 sm:p-10 flex flex-col items-center" data-testid="instagram-post-page">
      <div className="max-w-[560px] w-full">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-1">Asset</p>
            <h1 className="font-outfit text-2xl font-medium">Aavaa — Instagram post (1080 × 1350)</h1>
          </div>
          <button
            data-testid="ig-download-btn"
            onClick={download}
            className="flex items-center gap-2 rounded-full bg-[#8E9B82] hover:bg-[#7A8A6E] text-white h-10 px-5 font-outfit text-sm transition-colors"
          >
            <Download size={14} /> Download PNG
          </button>
        </div>
        <p className="font-manrope text-xs text-white/60 mb-5 leading-relaxed">
          Portrait 4:5 — Instagram's standard feed size. Right-click image to save, or click Download.
        </p>

        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
          {/* The wrapper `#ig-svg-wrap` is what gets exported at 1080x1350.
              We visually scale it down on screen via CSS transform but leave
              the source at native 1080x1350 so the PNG export stays pixel-perfect. */}
          <div className="relative" style={{ width: "100%", paddingTop: `${(1350 / 1080) * 100}%` }}>
            <div
              id="ig-svg-wrap"
              className="absolute top-0 left-0 origin-top-left"
              style={{
                width: 1080,
                height: 1350,
                fontFamily: "'Outfit', 'Manrope', system-ui, sans-serif",
                transform: "scale(var(--ig-scale, 1))",
              }}
              ref={(el) => {
                if (!el) return;
                const parent = el.parentElement;
                if (!parent) return;
                const setScale = () => {
                  const s = parent.clientWidth / 1080;
                  el.style.setProperty("--ig-scale", String(s));
                };
                setScale();
                const ro = new ResizeObserver(setScale);
                ro.observe(parent);
              }}
            >
              <InstagramSVG />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Design tokens (warm, not harsh black):
   INK     #3D4A3F   — primary "dark" text, softened deep forest
   SUBTLE  #6B7264   — body / subtitles
   MUTED   #8A8F80   — captions
   SAGE    gradient  — primary brand
   WARM    #D4A373   — accent
   ============================================================ */
function InstagramSVG() {
  const INK = "#3D4A3F";
  const SUBTLE = "#6B7264";
  const MUTED = "#8A8F80";

  return (
    <svg
      id="ig-svg"
      viewBox="0 0 1080 1350"
      xmlns="http://www.w3.org/2000/svg"
      width="1080"
      height="1350"
      className="block"
    >
      <defs>
        <linearGradient id="ig-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDFBF6" />
          <stop offset="55%" stopColor="#F7F2E8" />
          <stop offset="100%" stopColor="#EFE5D2" />
        </linearGradient>
        <radialGradient id="ig-halo-1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4A373" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#D4A373" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ig-halo-2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#8E9B82" stopOpacity="0.24" />
          <stop offset="100%" stopColor="#8E9B82" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ig-sage" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A7B399" />
          <stop offset="100%" stopColor="#7A8A6E" />
        </linearGradient>
        <linearGradient id="ig-floor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F3EFE6" />
          <stop offset="100%" stopColor="#E2DFD8" />
        </linearGradient>
        <filter id="ig-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="16" stdDeviation="24" floodColor="#3D4A3F" floodOpacity="0.1" />
        </filter>
      </defs>

      {/* Background */}
      <rect width="1080" height="1350" fill="url(#ig-bg)" />
      <ellipse cx="150" cy="140" rx="360" ry="300" fill="url(#ig-halo-1)" />
      <ellipse cx="940" cy="1240" rx="360" ry="300" fill="url(#ig-halo-2)" />

      {/* ======= Logo mark (top, left-aligned to match hero margin) ======= */}
      <g transform="translate(90,80)">
        <BrandMark />
      </g>

      {/* ======= Hero headline (full width, 2 lines) ======= */}
      <g transform="translate(90,210)">
        <text fontFamily="'Outfit', sans-serif" fontWeight="500" fill={INK} letterSpacing="-2.5">
          <tspan x="0" y="0" fontSize="74">Know how your child's day</tspan>
          <tspan x="0" y="92" fontSize="74"><tspan fontStyle="italic" fontWeight="300" fill="#8E9B82">really</tspan> went.</tspan>
        </text>
      </g>

      {/* ======= Subheadline ======= */}
      <g transform="translate(90,360)">
        <text fontFamily="'Manrope', sans-serif" fontSize="28" fontWeight="400" fill={SUBTLE}>
          <tspan x="0" dy="0">Aavaa watches your home camera and gives you custom</tspan>
          <tspan x="0" dy="42">summaries — <tspan fontWeight="600" fill={INK}>backed by real clips.</tspan></tspan>
        </text>
      </g>

      {/* ======= Stacked process cards (larger, vertically-centered text, no numbers) ======= */}
      {[
        {
          y: 470,
          title: "Your child plays",
          sub: "Your existing home camera quietly records.",
          ill: <RoomMini />,
        },
        {
          y: 704,
          title: "Aavaa analyzes the feed",
          sub: "AI watches for the behaviors you chose.",
          ill: <SphereMini />,
        },
        {
          y: 938,
          title: "You get a clip-backed summary",
          sub: "Every observation links to the exact moment.",
          ill: <PhoneMini />,
        },
      ].map((c, i) => (
        <g key={i} transform={`translate(90,${c.y})`}>
          <rect x="0" y="0" width="900" height="210" rx="32" fill="#FFFFFF" filter="url(#ig-shadow)" />
          <g transform="translate(26,16)">
            <rect x="0" y="0" width="280" height="178" rx="20" fill="#F7F2E8" />
            {c.ill}
          </g>
          <g transform="translate(338,96)">
            <text fontFamily="'Outfit', sans-serif" fontSize={i === 2 ? "40" : "44"} fontWeight="500" fill={INK} letterSpacing="-0.4">{c.title}</text>
            <text y="40" fontFamily="'Manrope', sans-serif" fontSize="22" fontWeight="400" fill={SUBTLE}>{c.sub}</text>
          </g>
        </g>
      ))}

      {/* ======= Chip row (centered, well-spaced) ======= */}
      <g transform="translate(0,1185)">
        <text x="540" y="0" textAnchor="middle" fontFamily="'Manrope', sans-serif" fontSize="16" fontWeight="600" fill={MUTED} letterSpacing="3">WHAT AAVAA CAN TRACK</text>
        {(() => {
          const chips = ["Throws stuff", "Eating habits", "Sleep", "Smiles & crying", "Focus", "Milestones"];
          const pad = 36;
          const charW = 12.5;
          const widths = chips.map((c) => c.length * charW + pad);
          const gap = 12;
          const total = widths.reduce((a, b) => a + b, 0) + gap * (chips.length - 1);
          let x = (1080 - total) / 2;
          const y = 32;
          return chips.map((c, i) => {
            const w = widths[i];
            const node = (
              <g key={i} transform={`translate(${x},${y})`}>
                <rect width={w} height="46" rx="23" fill="#FFFFFF" stroke="#E2DFD8" strokeWidth="1" />
                <text x={w / 2} y="29" textAnchor="middle" fontFamily="'Manrope', sans-serif" fontSize="19" fontWeight="500" fill={INK}>{c}</text>
              </g>
            );
            x += w + gap;
            return node;
          });
        })()}
      </g>

      {/* ======= URL footer (centered) ======= */}
      <text x="540" y="1300" textAnchor="middle" fontFamily="'Manrope', sans-serif" fontSize="24" fontWeight="600" fill={INK}>www.aavaa.ai</text>
    </svg>
  );
}

/* ==================== Mini illustrations (fit 280×178 box, centered) ==================== */
function RoomMini() {
  return (
    <g transform="translate(12,8)">
      {/* Floor */}
      <rect x="0" y="110" width="258" height="58" rx="0" fill="url(#ig-floor)" />
      {/* Camera (top-left) */}
      <g transform="translate(28,26)">
        <rect x="26" y="-11" width="5" height="11" fill="#B8AE98" />
        <rect x="0" y="0" width="56" height="32" rx="10" fill="url(#ig-sage)" />
        <circle cx="18" cy="16" r="9" fill="#3D4A3F" />
        <circle cx="18" cy="16" r="4.5" fill="#A7B399" />
        <circle cx="41" cy="10" r="2.8" fill="#D4A373" />
      </g>
      {/* Viewing cone */}
      <path d="M 62 58 L 26 162 L 232 162 Z" fill="#8E9B82" opacity="0.1" />
      {/* Toys */}
      <rect x="56" y="128" width="22" height="14" fill="#D4A373" rx="3" />
      <rect x="82" y="132" width="18" height="10" fill="#8E9B82" rx="2" />
      <rect x="104" y="126" width="16" height="16" fill="#C27E70" rx="2" />
      {/* Child (centered-right) */}
      <g transform="translate(142,52)">
        <ellipse cx="30" cy="85" rx="38" ry="7" fill="#3D4A3F" opacity="0.12" />
        <path d="M 0 78 Q 0 40 30 35 Q 60 40 60 78 Z" fill="#D4A373" />
        <circle cx="30" cy="22" r="23" fill="#EED3B5" />
        <path d="M 9 17 Q 30 -4 51 17 Q 47 7 30 5 Q 13 7 9 17 Z" fill="#6B4F3C" />
        {/* Glasses */}
        <circle cx="22" cy="25" r="5" fill="none" stroke="#3D4A3F" strokeWidth="1.5" />
        <circle cx="39" cy="25" r="5" fill="none" stroke="#3D4A3F" strokeWidth="1.5" />
        <line x1="27" y1="25" x2="34" y2="25" stroke="#3D4A3F" strokeWidth="1.5" />
        {/* Smile */}
        <path d="M 24 32 Q 30 37 37 32" fill="none" stroke="#3D4A3F" strokeWidth="1.5" strokeLinecap="round" />
      </g>
    </g>
  );
}

function SphereMini() {
  return (
    <g transform="translate(0,0)">
      <radialGradient id="sphere-halo" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#D4A373" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#D4A373" stopOpacity="0" />
      </radialGradient>
      <circle cx="140" cy="89" r="100" fill="url(#sphere-halo)" />
      {/* Sphere centered, bigger */}
      <circle cx="140" cy="89" r="62" fill="url(#ig-sage)" />
      <circle cx="140" cy="89" r="46" fill="none" stroke="#FFFFFF" strokeWidth="4" strokeDasharray="6 5" />
      {/* Sparkle */}
      <path d="M 140 67 L 146 83 L 162 89 L 146 95 L 140 111 L 134 95 L 118 89 L 134 83 Z" fill="#D4A373" />
      {/* 3 orbit dots — 120° apart at radius 80 */}
      <circle cx="140" cy="9" r="8" fill="#D4A373" stroke="#FFFFFF" strokeWidth="3" />
      <circle cx="71" cy="129" r="8" fill="#D4A373" stroke="#FFFFFF" strokeWidth="3" />
      <circle cx="209" cy="129" r="8" fill="#D4A373" stroke="#FFFFFF" strokeWidth="3" />
    </g>
  );
}

function PhoneMini() {
  return (
    <g transform="translate(85,10)">
      {/* Phone — 110×158 */}
      <rect x="0" y="0" width="110" height="158" rx="18" fill="#1F2420" />
      <rect x="5" y="5" width="100" height="148" rx="14" fill="#F7F2E8" />
      {/* Header text */}
      <rect x="12" y="15" width="58" height="7" rx="2.5" fill="#3D4A3F" />
      <rect x="12" y="27" width="36" height="4" rx="2" fill="#D4A373" />
      {/* 3 observation rows */}
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(11, ${40 + i * 34})`}>
          <rect width="88" height="28" rx="5" fill="#FFFFFF" stroke="#E2DFD8" strokeWidth="0.5" />
          <rect x="6" y="6" width="44" height="3.5" rx="1.5" fill="#6B7264" opacity="0.5" />
          <rect x="6" y="12" width="34" height="4" rx="1.5" fill="#3D4A3F" />
          <rect x="79" y="5" width="5" height="18" rx="2.5" fill={i === 1 ? "#D4A373" : "#8E9B82"} />
          <rect x="6" y="19" width="14" height="5" rx="1.2" fill={i === 1 ? "#D4A373" : "#8E9B82"} />
          <rect x="22" y="19" width="14" height="5" rx="1.2" fill={i === 1 ? "#E7C19A" : "#A7B399"} />
        </g>
      ))}
    </g>
  );
}

/**
 * BrandMark — the official Aavaa logo + wordmark (for IG post header).
 * Matches /components/AavaaLogo.jsx
 */
function BrandMark() {
  return (
    <g data-testid="ig-brand-mark">
      <defs>
        <linearGradient id="brand-body" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDFBF6" />
          <stop offset="100%" stopColor="#F3EFE6" />
        </linearGradient>
        <linearGradient id="brand-sage" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A7B399" />
          <stop offset="100%" stopColor="#7A8A6E" />
        </linearGradient>
        <linearGradient id="brand-warm" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E7C19A" />
          <stop offset="100%" stopColor="#D4A373" />
        </linearGradient>
      </defs>
      {/* Mark — 60x60 */}
      <g>
        <rect x="0" y="0" width="60" height="60" rx="17" fill="url(#brand-body)" stroke="#E2DFD8" strokeWidth="1" />
        <circle cx="22.5" cy="30" r="12" fill="url(#brand-sage)" />
        <circle cx="37.5" cy="30" r="12" fill="url(#brand-warm)" />
        <path d="M 30 20 a 12 12 0 0 1 0 20 a 12 12 0 0 1 0 -20 Z" fill="#FDFBF6" opacity="0.28" />
        <circle cx="22.5" cy="30" r="2" fill="#3D4A3F" />
        <circle cx="37.5" cy="30" r="2" fill="#3D4A3F" />
      </g>
      {/* Wordmark next to mark */}
      <text x="74" y="40" fontFamily="'Outfit', sans-serif" fontSize="34" fontWeight="600" fill="#3D4A3F" letterSpacing="-0.3">aavaa</text>
    </g>
  );
}
